import type { AriaAttributes } from 'react';
import { useRef } from 'react';
import { FocusOn } from 'react-focus-on';
import classNames from 'classnames';

import styles from './Drawer.module.css';

interface BaseDrawerProps extends AriaAttributes {
  /** Optional unique identifier for the drawer */
  id?: string;
  /** Controls whether the drawer is open or closed */
  isOpen: boolean;
  /** Content to be displayed inside the drawer */
  children: React.ReactNode;
}

interface ClosableDrawerProps extends BaseDrawerProps {
  /** Callback function called when the drawer should close */
  onClose: () => void;
  /** Description for screen readers when the drawer cannot be closed - not provided for closable drawers */
  lockedDescription?: never;
}

interface NonClosableDrawerProps extends BaseDrawerProps {
  /** Callback function called when the drawer should close - not provided for non-closable drawers */
  onClose?: never;
  /** Description for screen readers when the drawer cannot be closed (required when not closable) */
  lockedDescription: string;
}

type DrawerProps = ClosableDrawerProps | NonClosableDrawerProps;

export const Drawer = (props: DrawerProps) => {
  const { id, isOpen, onClose, children, lockedDescription } = props;

  const startY = useRef<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    startY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (startY.current === null) {
      return;
    }

    const endY = e.changedTouches[0].clientY;
    const deltaY = endY - startY.current;

    if (deltaY > 50) {
      onClose?.();
    }

    startY.current = null;
  };

  return (
    <FocusOn enabled={isOpen} focusLock scrollLock onEscapeKey={onClose}>
      <div
        data-testid="drawer-container"
        className={classNames(styles.container, { [styles.open]: isOpen })}
      >
        {onClose ? (
          <button
            onClick={onClose}
            className={classNames(styles.overlay, { [styles.open]: isOpen })}
            aria-label="Close dialog"
          />
        ) : (
          <div
            className={classNames(styles.overlay, { [styles.open]: isOpen })}
            role="presentation"
          />
        )}
        <div
          id={id}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          aria-modal={true}
          aria-hidden={!isOpen}
          aria-labelledby={props['aria-labelledby']}
          aria-description={onClose ? undefined : lockedDescription}
          className={classNames(styles.slidingPanel, {
            [styles.open]: isOpen,
          })}
        >
          {children}
        </div>
      </div>
    </FocusOn>
  );
};
